import { useEffect, useRef } from 'react';
import {
  createFileRoute,
  Navigate,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  handleGetGeometryGeometriesGeometryIdGetOptions,
  handleGetStudyStudiesStudyIdGetOptions,
} from '../../../api/generated/@tanstack/react-query.gen';
import { useMeshGeometryFallback } from '@/hooks/useMeshGeometryFallback';
import { UUID_REGEX } from '@/lib/uuid';
import {
  canEditMeshWorkspace,
  httpStatus,
  isBelowEditorRole,
} from '@/utils/shareAccess';
import { useGeometryLineage } from '@/hooks/useGeometryLineage';
import { useRecordRecentView } from '@/hooks/useRecordRecentView';
import { recentRemoveAction } from '@/hooks/recentRemoveAction';
import { useStaleWarning } from '@/hooks/useStaleWarning';
import { useUploadRevision } from '@/hooks/useUploadRevision';
import { useStore } from '@/stores/useStore';
import {
  GeometryNotFound,
  GeometryErrorBoundary,
} from '../-components/GeometryNotFound';
import { MeshGenWorkspace } from './-components/MeshGenWorkspace';
import { meshGenWorkspaceLoader } from './-loader';
import { StudyMeshChrome } from '../simops/-components/meshing/StudyMeshChrome';
import { isStudyMeshingReadOnly } from '../simops/-components/meshing/status';
import { WorkspaceReadOnlyProvider } from '@/features/workspace-readonly';
import { CenteredSpinner } from '@/components/CenteredSpinner';
import { StudyDraftMeshChrome } from '../simops/-components/meshing/StudyDraftMeshChrome';
import { parseEntitlementDenial } from '@/lib/entitlementDenial';
import {
  meshGenDenialText,
  useMeshGenDenial,
} from '@/hooks/useMeshGenAccessNotice';

const uuidParam = z.string().regex(UUID_REGEX).optional().catch(undefined);

const meshGenSearchSchema = z.object({
  mesh: uuidParam,
  // Selected geometry version in the lineage. Absent ⇒ the workspace falls
  // back to the lineage tip. Synced bidirectionally with `activeGeometryId`
  // in MeshGenWorkspace, and only written when a non-tip version is selected
  // so a normal open stays `/meshgen/<uuid>`.
  geometry: uuidParam,
  // Study context — set when MeshGen is opened from inside a SimOps
  // user-meshing phase. Both must be set for the study chrome to render;
  // a malformed value (non-UUID) is silently dropped via `.catch`.
  study: uuidParam,
  dp: uuidParam,
  // Set only on links from the "Shared with me" tab (ENG-2009), so a 404 there
  // can say "the owner stopped sharing this link" instead of the generic
  // doesn't-exist-or-no-access copy — for a row the user reached FROM a list
  // of things that used to work, the revoked case is the likely one.
  from: z.literal('recent').optional().catch(undefined),
});

// Root project ids with a stale-warning re-upload in flight. Module-scope so
// the guard survives route remounts (navigate away and back mid-upload) —
// mutation isPending can't cover this: it resets on remount and doesn't span
// the gzip + S3 PUT phases anyway.
const revisionUploadsInFlight = new Set<string>();

// Opens a native file picker for a revision file. Matches the accept list of
// the established upload-revision control (GeometryHealthPanel) — CAD formats
// plus STL, which the revision pipeline now handles — not the new-upload
// dropzone list.
function pickGeometryFile(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.step,.stp,.iges,.igs,.brep,.stl';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    // `change` never fires when the user dismisses the native dialog; without
    // this the promise would stay pending forever.
    input.oncancel = () => resolve(null);
    input.click();
  });
}

function UuidRoute() {
  const { t } = useTranslation();
  const { uuid } = useParams({ from: '/_app/meshgen/$uuid' });
  const { study, dp, from } = useSearch({ from: '/_app/meshgen/$uuid' });
  const navigate = useNavigate();
  const { uploadRevision } = useUploadRevision(uuid);
  const meshgenDenial = useMeshGenDenial();
  // Read when the upload finishes, not when the stale toast fired: that toast
  // can fire before GET /subscription has landed.
  const meshgenDenialRef = useRef(meshgenDenial);
  useEffect(() => {
    meshgenDenialRef.current = meshgenDenial;
  });
  const showToast = useStore((s) => s.showToast);
  const isValidUuid = UUID_REGEX.test(uuid);

  const {
    data: geometry,
    error,
    isError,
    isFetchedAfterMount,
  } = useQuery({
    ...handleGetGeometryGeometriesGeometryIdGetOptions({
      path: { geometry_id: uuid },
    }),
    retry: false,
    enabled: isValidUuid,
  });
  const recentIdentity = useRecordRecentView({
    kind: 'geometry',
    id: isValidUuid ? uuid : null,
    name: geometry?.filename,
    myRole: geometry?.my_role,
  });

  // Staleness is judged by the lineage TIP (the version the workspace actually
  // displays), not the immutable root: a re-upload adds a new tip, so taking
  // the suggested action clears the warning on the next open. The lineage
  // query is shared with the workspace tree, so this hits the same cache.
  const lineageQuery = useGeometryLineage(isValidUuid ? uuid : null);
  const lineageTip = lineageQuery.data?.versions?.at(-1);
  // Suppressed in SimOps study mode (`?study=`): the study flow owns its own
  // stale warning, and re-uploading here would silently change the lineage a
  // study references.
  const staleTimestamp = study
    ? undefined
    : (lineageTip?.created_at ?? geometry?.created_at);
  // Older projects were diagnosed and repaired by an older pipeline, and
  // meshing sits on top of that, so re-meshing can fail. Let the user re-upload
  // the file in place: that runs a fresh diagnosis and repair before meshing.
  // Direct upload (not revealControl) because MeshGen has no mounted
  // upload-revision control to reveal — the health panel is hidden here
  // (ENG-932).
  useStaleWarning(staleTimestamp, t('compCommon.staleWarning.messageProject'), {
    label: t('compCommon.staleWarning.actionReupload'),
    onAction: () => {
      if (revisionUploadsInFlight.has(uuid)) return;
      void pickGeometryFile().then((file) => {
        if (!file) return;
        revisionUploadsInFlight.add(uuid);
        showToast(t('meshgen.upload.uploading'));
        void uploadRevision(uuid, file)
          .then(({ geometryId: newGeometryId, diagnosisError }) => {
            // Switch the workspace to the version we just uploaded, using the
            // composite preview action: it pins the new id AND clears any
            // selected mesh. A selected mesh renders in priority over the
            // geometry preview, so setting the pin alone left the viewer on
            // the old version's mesh. Pinning the concrete id (not null) also
            // avoids racing the sidebar's tip-seeding against the lineage
            // refetch, which would re-pin the still-cached old tip.
            // Only steer the workspace if the user is still on this project:
            // the upload can take minutes and this closure outlives the route,
            // so an unguarded call would pin this lineage's geometry into
            // whichever project is now open (and Generate Mesh submits off
            // activeGeometryId).
            if (useStore.getState().currentProject === uuid) {
              useStore.getState().showGeometryPreview(newGeometryId);
            }
            // A refused diagnose must not read as "running a fresh
            // diagnosis": say which seat or subscription is missing, named
            // for MeshGen when MeshGen is the one missing (ENG-3091).
            const refused = parseEntitlementDenial(diagnosisError);
            showToast(
              (refused &&
                meshGenDenialText(meshgenDenialRef.current, refused)) ||
                t('meshgen.upload.uploaded'),
            );
          })
          .catch(() => showToast(t('meshgen.upload.failed')))
          .finally(() => revisionUploadsInFlight.delete(uuid));
      });
    },
  });

  const studyQuery = useQuery({
    ...handleGetStudyStudiesStudyIdGetOptions({
      path: { study_id: study ?? '' },
    }),
    enabled: !!study,
    retry: false,
  });

  // By status, not message: a 404 arrives as the parsed error body, so the
  // message check this replaced never fired and a missing geometry loaded an
  // empty workspace instead of saying so.
  const is404 = httpStatus(error) === 404;
  // A mesh id where the geometry belongs (ENG-2669). A "Shared with me" row is
  // always a geometry, so it keeps its revoked-link copy without the extra read.
  const meshFallback = useMeshGeometryFallback(
    uuid,
    isValidUuid && is404 && from !== 'recent',
  );

  if (!isValidUuid) {
    return <GeometryNotFound message={t('meshgen.route.invalidUrl')} />;
  }

  if (isError) {
    if (is404) {
      if (meshFallback.rootId) {
        return (
          <Navigate
            to="/meshgen/$uuid"
            params={{ uuid: meshFallback.rootId }}
            // Keep the rest (`?study=&dp=`), so a study-scoped link still
            // opens the study's meshing view.
            search={(prev) => ({ ...prev, mesh: uuid })}
            replace
          />
        );
      }
      if (meshFallback.pending) return null;
      // Arrived from the "Shared with me" tab: this URL worked once, so the
      // likely cause is the owner revoking the link, not a bad id. Offer to
      // drop the dead row while we have the user here.
      return from === 'recent' ? (
        <GeometryNotFound
          message={t('meshgen.route.shareRevoked')}
          secondaryAction={recentRemoveAction(
            recentIdentity,
            'geometry',
            uuid,
            t('compProductLibrary.recentlyViewed.remove'),
            () => void navigate({ to: '/meshgen' }),
          )}
        />
      ) : (
        <GeometryNotFound message={t('meshgen.route.notFound')} />
      );
    }
    // Non-404 errors (500, network down) — let the workspace load; it
    // will surface its own error states and recover when the backend comes back.
  }

  // Below-editor roles (viewer/commenter) get the read-only share viewer
  // instead of the editable MeshGen workspace (ENG-1068). Owners/editors are
  // unaffected. Scoped to the standalone route only — the study-chrome modes
  // below (`study && dp` / draft mesh-in) are reached through SimOps's own
  // study-role gating, so this doesn't double-gate a study editor who isn't
  // separately an editor on the underlying geometry.
  if (!study && geometry && isBelowEditorRole(geometry.my_role)) {
    // A role cached before this mount can be another session's: a signed-out
    // visit to the public share page caches `viewer` under this same key.
    // Only this mount's own read may send the reader away (ENG-2687).
    if (!isFetchedAfterMount) return null;
    // Keep the mesh and version the link named; without them the viewer
    // opens the latest completed mesh instead.
    return (
      <Navigate
        to="/share/geometry/$uuid"
        params={{ uuid }}
        search={(prev) => ({ mesh: prev.mesh, geometry: prev.geometry })}
        replace
      />
    );
  }

  const inStudy = !!study && (!!dp || studyQuery.data?.status === 'draft');
  const agentControlsAllowed = canEditMeshWorkspace({
    geometryRole: geometry?.my_role,
    studyRole: studyQuery.data?.my_role,
    inStudy,
  });

  // `key={uuid}` forces MeshGenWorkspace to remount when the user switches
  // DPs in study mode (which changes the geometry); useInitialProjectLoad's
  // ref-gated load wouldn't refire otherwise.
  if (study && dp) {
    // Read-only unless the study is waiting on these meshes and the reader can
    // edit it (`isStudyMeshingReadOnly`) — past meshing, or for a viewer, the
    // meshes open to look at rather than not at all. A study that fails to
    // load reads as read-only; the chrome says why. The workspace waits for
    // the answer instead of mounting editable and flipping, because its
    // read-only mode changes what it does on mount (the mesh it auto-selects,
    // whether it restores and persists drafts).
    const meshReadOnly =
      !studyQuery.data || isStudyMeshingReadOnly(studyQuery.data);
    return (
      <StudyMeshChrome studyId={study} dpId={dp} readOnly={meshReadOnly}>
        {studyQuery.isPending ? (
          <CenteredSpinner />
        ) : (
          <WorkspaceReadOnlyProvider value={meshReadOnly}>
            <MeshGenWorkspace
              key={uuid}
              projectId={uuid}
              showLibraryBack={false}
              agentControlsAllowed={agentControlsAllowed && !meshReadOnly}
            />
          </WorkspaceReadOnlyProvider>
        )}
      </StudyMeshChrome>
    );
  }

  // A draft's mesh-in link carries `?study=` and no dp: until the study is read
  // we cannot tell a draft from any other study. Wait, rather than mount the
  // standalone workspace and swap it for the draft chrome a moment later —
  // the swap remounts the workspace and re-runs its on-mount load (ENG-3100).
  if (study && !dp && studyQuery.isPending) return <CenteredSpinner />;

  if (study && !dp && studyQuery.data?.status === 'draft') {
    return (
      <StudyDraftMeshChrome studyId={study} geometryId={uuid}>
        <MeshGenWorkspace
          key={uuid}
          projectId={uuid}
          showLibraryBack={false}
          agentControlsAllowed={agentControlsAllowed}
        />
      </StudyDraftMeshChrome>
    );
  }

  return (
    <MeshGenWorkspace
      key={uuid}
      projectId={uuid}
      showLibraryBack
      agentControlsAllowed={agentControlsAllowed}
    />
  );
}

export const Route = createFileRoute('/_app/meshgen/$uuid')({
  validateSearch: meshGenSearchSchema,
  // Warm the geometry's meshes + diagnosis so a dashboard row click lands the
  // workspace on content instead of spinners. Best-effort: the component keeps
  // its own useQuery retry/error UI. See `./-loader`.
  loader: meshGenWorkspaceLoader,
  component: UuidRoute,
  errorComponent: GeometryErrorBoundary,
});
